<template>
  <Component
    :is="components[icon]"
    class="nds-icon"
    :style="{
      color,
      stroke: color,
      'font-size': `${size}px`,
      'line-height': `${size}px`,
      width: `${size}px`,
      height: `${size}px`,
    }"
    @click="emit('click', $event)"
  />
</template>

<script setup lang="ts">
import {
  ArrowDown,
  ArrowDownToLine,
  ArrowLeft,
  ArrowRight,
  ArrowRightToLine,
  ArrowUpDown,
  ArrowUpFromLine,
  ArrowUp,
  Box,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUp,
  ChevronsUpDown,
  CircleAlert,
  CircleHelp,
  CircleX,
  OctagonAlert,
  Flame,
  EllipsisVertical,
  Globe,
  Info,
  IterationCw,
  Link,
  Link2Off,
  MessageCircleWarning,
  MessageSquare,
  MessageSquarePlus,
  Network,
  Pencil,
  Plus,
  SquareArrowOutUpRight,
  SquareCheck,
  Trash2,
  TrendingUp,
  X,
  Play,
  SkipForward,
  LineChart,
  NotebookPen,
  Pen,
  CalendarDays,
  Search,
  CornerDownRight,
  File,
  Copy,
  Move,
  UserCircle,
  Users,
  TrafficCone,
  ClipboardCheck,
  Gauge,
  Boxes,
  Trello,
  List,
  ChartPie,
  Timer,
  MoreVertical,
  Mail,
  AlertTriangle,
  Eye,
  EyeOff,
  Lightbulb,
  Cog,
  Settings,
  MessageCircleQuestion,
  CircleDotDashed,
  ShieldCheck,
  Tags,
  Send,
  Lock,
  HardHat,
  Filter,
  Layers,
} from 'lucide-vue'
import FunnelPlus from '@/components/NewDesignSystem/NdsIcon/backported-icons/FunnelPlus.vue'
import type { Component, PropType } from 'vue'
import { type NdsIconType, NdsIconSize } from '@/components/NewDesignSystem/NdsIcon/NdsIcon.interfaces'
import { NdsColor } from '@/interfaces/NdsColors.interfaces'

const emit = defineEmits<(e: 'click', event: MouseEvent) => void>()

defineProps({
  icon: {
    type: String as PropType<NdsIconType>,
    required: true,
  },
  color: {
    type: String as PropType<NdsColor>,
    default: NdsColor.BLACK,
  },
  size: {
    type: Number as PropType<NdsIconSize>,
    default: NdsIconSize.M,
  },
})

const components: Record<NdsIconType, Component> = {
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUp,
  ChevronsUpDown,
  CircleX,
  CircleHelp,
  ArrowLeft,
  ArrowRight,
  ArrowDown,
  ArrowDownToLine,
  ArrowUpDown,
  ArrowUpFromLine,
  ArrowUp,
  Plus,
  EllipsisVertical,
  X,
  Pencil,
  Link,
  Link2Off,
  Globe,
  Check,
  SquareCheck,
  CircleAlert,
  OctagonAlert,
  Flame,
  MessageSquarePlus,
  Info,
  SquareArrowOutUpRight,
  Trash2,
  TrendingUp,
  IterationCw,
  ArrowRightToLine,
  MessageCircleWarning,
  MessageSquare,
  Play,
  SkipForward,
  Search,
  LineChart,
  NotebookPen,
  Pen,
  CalendarDays,
  CornerDownRight,
  File,
  Network,
  Copy,
  Move,
  UserCircle,
  Users,
  TrafficCone,
  ClipboardCheck,
  Gauge,
  Boxes,
  Box,
  Trello,
  List,
  ChartPie,
  Timer,
  MoreVertical,
  Mail,
  AlertTriangle,
  Eye,
  EyeOff,
  Lightbulb,
  Cog,
  Settings,
  MessageCircleQuestion,
  CircleDotDashed,
  ShieldCheck,
  Tags,
  Send,
  Lock,
  HardHat,
  FunnelPlus,
  Filter,
  Layers,
}
</script>

<style lang="postcss" scoped>
.nds-icon {
  stroke-width: 1.25px;
  min-width: fit-content;
}
</style>
